import { jsxs, jsx } from 'react/jsx-runtime';
import { forwardRef, useState, useRef, useEffect, useCallback } from 'react';
import { formatTime } from '../../helpers/time.mjs';
import { useTestMediaElementVolumeSetter } from '../../hooks/useTestMediaElementVolumeSetter.mjs';
import './assets/back.svg.mjs';
import './assets/close.svg.mjs';
import './assets/fullscreen-on.svg.mjs';
import './assets/pause.svg.mjs';
import './assets/play.svg.mjs';
import './assets/volume-off.svg.mjs';
import './assets/volume-on.svg.mjs';
import { Wrap, IconWrap, Icon, Video, ControlWrap, TimelineWrap, Timeline, Controls, LeftSide, IconButton, VolumeWrap, VolumeSliderWrap, VolumeSlider, Time, Center, RightSide } from './VideoPlayer.styles.mjs';
import CloseIcon__default from "../../assets/close.svg";
import BackIcon__default from "../../assets/back.svg";
import PauseIcon__default from "../../assets/pause.svg";
import PlayIcon__default from "../../assets/play.svg";
import VolumeOffIcon__default from "../../assets/volume-off.svg";
import VolumeOnIcon__default from "../../assets/volume-on.svg";
import FullscreenOnIcon__default from "../../assets/fullscreen-on.svg";
"use strict";
const VideoPlayer = forwardRef(function VideoPlayer2({
  isOpen,
  title,
  src,
  poster,
  onClose,
  className,
  showCloseButton = false,
  motif = "dark"
}, ref) {
  const [isPlaying, setIsPlaying] = useState(true);
  const localVideoRef = useRef(null);
  const videoRef = ref ?? localVideoRef;
  const [currentTimeSec, setCurrentTimeSec] = useState(0);
  const [durationSec, setDurationSec] = useState(0);
  const isVolumeChangeSupported = useTestMediaElementVolumeSetter();
  useEffect(() => {
    if (!videoRef || !("current" in videoRef) || !videoRef.current) {
      return;
    }
    const video = videoRef.current;
    const handleFullscreenChange = () => {
      video.playsInline = true;
      video.pause();
      video.removeAttribute("controls");
    };
    video?.addEventListener("webkitendfullscreen", handleFullscreenChange);
    return () => {
      video?.removeEventListener("webkitendfullscreen", handleFullscreenChange);
    };
  }, [videoRef]);
  useEffect(() => {
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef?.current;
    const handleClose = () => {
      setTimeout(() => {
        onClose?.();
      }, 500);
    };
    video?.addEventListener("ended", handleClose);
    return () => {
      video?.removeEventListener("ended", handleClose);
    };
  }, [videoRef, onClose]);
  useEffect(() => {
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    const updateTime = () => {
      setCurrentTimeSec(video?.currentTime || 0);
    };
    video?.addEventListener("timeupdate", updateTime);
    return () => {
      video?.removeEventListener("timeupdate", updateTime);
    };
  }, [videoRef]);
  const handlePlay = useCallback(() => {
    try {
      if (videoRef && "current" in videoRef) {
        videoRef?.current?.play();
      }
      setIsPlaying(true);
    } catch (e) {
      console.error(e);
    }
  }, [videoRef]);
  const handlePause = useCallback(() => {
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    try {
      videoRef.current?.pause();
      setIsPlaying(false);
    } catch (e) {
      console.log(e);
    }
  }, [videoRef]);
  useEffect(() => {
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    if (!isOpen) {
      handlePause();
      setCurrentTimeSec(0);
      if (video) {
        video.currentTime = 0;
      }
    } else {
      handlePlay();
    }
  }, [handlePause, handlePlay, isOpen, videoRef]);
  const handlePlayback = useCallback(e => {
    e?.stopPropagation();
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    if (videoRef.current?.paused) {
      handlePlay();
    } else {
      handlePause();
    }
  }, [handlePause, handlePlay, videoRef]);
  const handleFullscreen = useCallback(async e => {
    e.stopPropagation();
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    video.playsInline = false;
    if (video.requestFullscreen) {
      try {
        await video.requestFullscreen();
      } catch (e2) {
        console.log(e2);
      }
    } else if (video.paused) {
      try {
        video.play();
        video.pause();
      } catch (e2) {
        console.log(e2);
      }
    } else {
      try {
        video.pause();
        video.play();
      } catch (e2) {
        console.log(e2);
      }
    }
  }, [videoRef]);
  const handleSeek = useCallback(e => {
    e.stopPropagation();
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    const time = Number(e.target.value);
    if (video) {
      video.currentTime = time;
      setCurrentTimeSec(time);
    }
  }, [videoRef]);
  const [isMuted, setIsMuted] = useState(false);
  const [volume, setVolume] = useState(1);
  const handleVolumeClick = useCallback(e => {
    e.stopPropagation();
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    if (!video) {
      return;
    }
    if (isMuted) {
      video.muted = false;
      setIsMuted(false);
    } else {
      video.muted = true;
      setIsMuted(true);
    }
  }, [isMuted, videoRef]);
  const handleVolumeChange = useCallback(e => {
    e.stopPropagation();
    if (!videoRef || !("current" in videoRef)) {
      return;
    }
    const video = videoRef.current;
    const volume2 = Number(e.target.value);
    if (video) {
      if (volume2 > 0) {
        video.volume = volume2;
        setVolume(volume2);
      }
      if (volume2 === 0) {
        video.muted = true;
        setIsMuted(true);
      } else {
        video.muted = false;
        setIsMuted(false);
      }
    }
  }, [videoRef]);
  return /* @__PURE__ */jsxs(Wrap, {
    isOpen,
    onClick: () => handlePlayback(),
    className,
    children: [/* @__PURE__ */jsx(IconWrap, {
      onClick: onClose,
      $alignRight: showCloseButton,
      children: /* @__PURE__ */jsx(Icon, {
        $motif: motif,
        src: showCloseButton ? CloseIcon__default : BackIcon__default,
        height: 22,
        width: 22
      })
    }), /* @__PURE__ */jsx(Video, {
      ref: videoRef,
      src: src.hd,
      poster,
      controls: false,
      playsInline: true,
      controlsList: "nodownload",
      onPlay: () => setIsPlaying(true),
      onPause: () => setIsPlaying(false),
      onLoadedMetadata: e => setDurationSec(e.currentTarget.duration)
    }), /* @__PURE__ */jsxs(ControlWrap, {
      children: [/* @__PURE__ */jsx(TimelineWrap, {
        $motif: motif,
        onMouseDown: handlePause,
        onMouseUp: handlePlay,
        children: /* @__PURE__ */jsx(Timeline, {
          min: 0,
          max: durationSec,
          value: currentTimeSec,
          onChange: handleSeek,
          step: 0.01,
          width: "calc(100% + 12px)"
        })
      }), /* @__PURE__ */jsxs(Controls, {
        children: [/* @__PURE__ */jsxs(LeftSide, {
          children: [/* @__PURE__ */jsx(IconButton, {
            type: "button",
            onClick: handlePlayback,
            children: isPlaying ? /* @__PURE__ */jsx(Icon, {
              $motif: motif,
              src: PauseIcon__default,
              height: 22,
              width: 22
            }) : /* @__PURE__ */jsx(Icon, {
              $motif: motif,
              src: PlayIcon__default,
              height: 22,
              width: 22
            })
          }), /* @__PURE__ */jsxs(VolumeWrap, {
            children: [/* @__PURE__ */jsx(IconButton, {
              type: "button",
              onClick: handleVolumeClick,
              children: /* @__PURE__ */jsx(Icon, {
                $motif: motif,
                src: isMuted ? VolumeOffIcon__default : VolumeOnIcon__default,
                height: 22,
                width: 22
              })
            }), isVolumeChangeSupported && /* @__PURE__ */jsx(VolumeSliderWrap, {
              onClick: e => e.stopPropagation(),
              children: /* @__PURE__ */jsx(VolumeSlider, {
                min: 0,
                max: 1,
                step: 0.1,
                value: isMuted ? 0 : volume,
                onChange: handleVolumeChange,
                direction: "vertical",
                size: "large",
                width: 76
              })
            })]
          }), /* @__PURE__ */jsxs(Time, {
            $motif: motif,
            children: [formatTime(currentTimeSec), " / ", formatTime(durationSec)]
          })]
        }), /* @__PURE__ */jsx(Center, {
          $motif: motif,
          children: title
        }), /* @__PURE__ */jsx(RightSide, {
          children: /* @__PURE__ */jsx(IconButton, {
            type: "button",
            onClick: handleFullscreen,
            children: /* @__PURE__ */jsx(Icon, {
              $motif: motif,
              src: FullscreenOnIcon__default,
              height: 22,
              width: 22
            })
          })
        })]
      })]
    })]
  });
});
export { VideoPlayer };
//# sourceMappingURL=VideoPlayer.mjs.map
